import { type Styles } from '@adornis/ass/style.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { ChancenComponentFabTaskBar } from '@chancenwerk/components/chancen-component-fab-task-bar.js';
import type { AllPermissions } from '@chancenwerk/db/permissions.js';
import '@chancenwerk/design/components/cr-loader.js';
import type { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import '@chancenwerk/pages/chancen-page-no-permission.js';
import { html, nothing, type PropertyValueMap } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref, type Ref } from 'lit/directives/ref.js';
import './cr-flex.js';
import type { ChromiumFlex } from './cr-flex.js';

/**
 * @customElement cr-page
 *
 */
@customElement('cr-page')
export class CrPage extends ChemistryLitElement {
  @property({ attribute: false }) public permission?: AllPermissions | AllPermissions[];
  @property({ attribute: false }) public context = ANY_CONTEXT;
  @property({ type: Boolean, attribute: 'no-section' }) section?: boolean;
  @property({ type: Boolean, attribute: 'sticky-header' }) stickyHeader?: boolean;
  @property({ type: Boolean, attribute: 'no-bottom-padding' }) noBottomPadding?: boolean;
  @property({ type: Boolean, attribute: 'fixed-height' }) fixedHeight?: boolean;
  @property({ type: Boolean, attribute: 'footer' }) footerEnabled?: boolean;
  @property({ type: String, reflect: true }) label = '';
  @property({ type: String, reflect: true }) description = '';
  @property({ attribute: false }) backRoute: string | (() => void) | null = null;
  @property({ type: Boolean, reflect: true }) topScrollButton = false;

  @state() protected readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());
  protected readonly _anchor: Ref<ChromiumFlex> = createRef<ChromiumFlex>();

  protected readonly _header: Ref<ChromiumFlex> = createRef();

  @property({ attribute: false }) public loader = html`
    <cr-flex center crossaxis-center>
      <cr-loader></cr-loader>
    </cr-flex>
  `;

  @state() size = new WindowSizeController(this);

  override styles(): Styles[] {
    return [
      ...super.styles(),
      {
        ':host': {
          height: '100%',
          overflowX: 'hidden',
        },
        '.header': {
          top: this.stickyHeader ? '0' : 'unset',
          position: this.stickyHeader ? 'sticky' : 'unset',
          background: this.colors.neutral.shade(50),

          padding: this.size.mobile ? this.spacing.xs : this.spacing.sm,
        },
        '.footer': {
          bottom: '0',
          position: 'sticky',
        },
      },
    ];
  }

  override render() {
    if (this.permission) {
      if (Array.isArray(this.permission)) {
        const hasAnyPermission = this.permission.some(p => this._currentUser.value?.hasPermission(p, this.context));
        return hasAnyPermission
          ? this._contentWrapper()
          : html` <chancen-page-no-permission></chancen-page-no-permission> `;
      }
      return permissionGuard(this.permission, () => this._contentWrapper(), {
        forbiddenTemplate: html` <chancen-page-no-permission></chancen-page-no-permission> `,
        context: this.context,
      });
    }
    return this._contentWrapper();
  }

  protected override firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.firstUpdated(_changedProperties);
    if (this.topScrollButton) {
      ChancenComponentFabTaskBar.addItem({
        _id: 'top_scroll',
        rendereable: this._topScrollButton(),
      });
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();

    if (this.topScrollButton) {
      ChancenComponentFabTaskBar.removeItemWithID('top_scroll');
    }
  }

  private _headerWrapper() {
    return html`
      <cr-flex ${ref(this._header)} space="${this.size.mobile ? 'sm' : 'md'}" class="header" ${css({ zIndex: 100 })}>
        <cr-flex
          horizontal
          space-between
          wrap
          ${css(
            this.backRoute && this.section
              ? {
                  marginLeft:
                    this.size.landscape && (this.size.width ?? 0) >= 1430 ? `calc(-${this.spacing.md} - 25px)` : `0`,
                }
              : {},
          )}
          space="${this.size.mobile ? 'xs' : 'sm'}"
        >
          <cr-flex space="${this.size.mobile ? 'xs' : 'sm'}" horizontal crossaxis-center flex ${css({ flexShrink: 1 })}>
            ${this.backRoute
              ? html`
                  <cr-button
                    mode="icon"
                    icon="arrow_back_ios"
                    @click=${() => {
                      if (typeof this.backRoute === 'function') {
                        this.backRoute();
                      } else if (this.backRoute) {
                        goTo(this.backRoute);
                      }
                    }}
                  >
                  </cr-button>
                `
              : nothing}

            <cr-h2>${this.label}</cr-h2>
          </cr-flex>
          ${this.header()}
        </cr-flex>
        ${this.descriptionTemplate()}
      </cr-flex>
    `;
  }

  private _contentWrapper() {
    return html`
      ${this.topScrollButton ? html` <cr-flex ${ref(this._anchor)}></cr-flex> ` : nothing}
      <cr-flex
        ${css({
          height: this.fixedHeight ? '100%' : 'unset',
        })}
      >
        ${this._section()}
      </cr-flex>
    `;
  }

  protected _section() {
    return html`
      ${!this.section
        ? html`
            ${this._headerWrapper()} ${this.content()}
            ${this.noBottomPadding
              ? nothing
              : html`
                  <cr-flex ${css({ paddingBottom: this.size.mobile ? this.spacing.sm : this.spacing.xl })}></cr-flex>
                `}
            ${this.footerEnabled ? this._footerWrapper() : nothing}
          `
        : html`
            <cr-section
              ${css({
                paddingBottom: !this.noBottomPadding ? (this.size.mobile ? this.spacing.sm : this.spacing.xl) : '0px',
              })}
            >
              ${this._headerWrapper()} ${this.content()} ${this.footerEnabled ? this._footerWrapper() : nothing}
            </cr-section>
          `}
    `;
  }

  protected _topScrollButton() {
    return html`
      ${this.topScrollButton
        ? html`
            <cr-button
              mode="fab"
              over-animation="jump"
              icon="chevron_right"
              @click=${() =>
                this._anchor.value?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })}
              ${css({
                transform: 'rotate(-90deg)',
              })}
            >
              <cr-tooltip>
                <cr-text> Nach oben </cr-text>
              </cr-tooltip>
            </cr-button>
          `
        : nothing}
    `;
  }

  private _footerWrapper() {
    return html` <cr-flex ${css({ height: '700px' })}></cr-flex> <cr-flex class="footer"> ${this.footer()} </cr-flex> `;
  }

  protected descriptionTemplate(): Renderable {
    return this.description.length
      ? html` <cr-text ${css({ maxWidth: this.sizes.maxReadableWidth })}> ${this.description} </cr-text> `
      : nothing;
  }

  protected header(): Renderable {
    return html`<x-flex></x-flex>`;
  }

  protected content(): Renderable {
    return html`<x-flex></x-flex>`;
  }

  protected footer(): Renderable {
    return html``;
  }
}
